import type { ComponentProps } from 'react';
import Link from 'next/link';

import { TeamAvatar, TeamUserAvatar } from '@poseidon/components/Avatar';
import { cn } from '@poseidon/components/cn';
import { Tooltip } from '@poseidon/components/Input2';

import { dataOnboardingIds } from '~/components/Onboarding/onboarding.model';

interface UserLogoutRowProps {
  authData: NonNullable<ComponentProps<typeof TeamUserAvatar>['userTeam']> & {
    teamName: string;
    teamLogo: ComponentProps<typeof TeamAvatar>['logo'];
  };
  minimized?: boolean;
}

// Pure: renders from props only. The tooltip's anchor/hover state lives inside
// the design-system `Tooltip` leaf (rendered here `as={Link}`) rather than in a
// hook in this component's body.
export const UserLogoutRow = ({ authData, minimized }: UserLogoutRowProps) => {
  const name = authData.userTeamName ?? authData.userEmail;
  const tooltip = minimized ? `${name} (${authData.teamName})` : undefined;

  return (
    <div className="whitespace-pre flex flex-row items-center gap-1 overflow-hidden pt-6">
      <Tooltip
        as={Link}
        tooltip={tooltip}
        href={`/teams/active/user`}
        data-onboarding={dataOnboardingIds.settingsTrigger}
        className={cn(
          'relative flex shrink grow basis-0 cursor-pointer flex-row gap-3 rounded-md p-2 transition-colors hover:bg-gray-100 dark:hover:bg-slate-900',
        )}>
        <TeamUserAvatar className="size-10" shape="circle" userTeam={authData} />

        <TeamAvatar
          className="absolute bottom-1 left-1 size-5"
          logo={authData.teamLogo}
          name={authData.teamName}
          shape="circle"
        />
        <div className="flex w-0 shrink grow basis-0 flex-col overflow-hidden text-ellipsis">
          <div className="max-h-5 overflow-hidden text-ellipsis text-sm font-semibold text-gray-700 dark:text-white">
            {name}
          </div>
          <div className="max-h-5 overflow-hidden text-ellipsis text-sm font-normal text-gray-500 dark:text-gray-400">
            {authData.teamName}
          </div>
        </div>
      </Tooltip>
    </div>
  );
};
